import { inject, Injectable } from '@angular/core';
import { Cart, ItemForDisplay, ItemInventoryType } from '../models';
import { AppStateService } from './app-state.service';
import { BehaviorSubject } from 'rxjs';
import { GoogleAnalyticsService } from './google-analytics.service';
import { GAEventAction } from '../enums';
import { getItemPrice } from '../pipes';

const CART_NAME = 'aim-cart';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  appStateService = inject(AppStateService);
  gaService = inject(GoogleAnalyticsService);

  private cartSubject = new BehaviorSubject<Cart>({ items: [] });
  public cart$ = this.cartSubject.asObservable();
  public getCart(): Cart {
    return this.cartSubject.value;
  }

  private cartTotalSubject = new BehaviorSubject<number>(0);
  public cartTotal$ = this.cartTotalSubject.asObservable();
  public getCartTotal(): number {
    return this.cartTotalSubject.value;
  }

  private cartHasSerializedItem = false;
  public hasCartGotSerializedItem(): boolean {
    return this.cartHasSerializedItem;
  }

  private cartHasAccessoryItem = false;
  public hasCartGotAccessoryItem(): boolean {
    return this.cartHasAccessoryItem;
  }

  constructor() {
    this.getCartFromStorage();
  }

  private getCartFromStorage() {
    if (this.appStateService.isBrowser) {
      const cartFromStorage = localStorage.getItem(CART_NAME);
      if (cartFromStorage) {
        const cart: Cart = JSON.parse(cartFromStorage);
        cart.items = cart.items?.filter(item => item.cartQty !== 0);
        this.updateCart(cart);
      }
    }
  }

  private saveCart() {
    localStorage.setItem(CART_NAME, JSON.stringify(this.cartSubject.value));
  }

  private createCart() {
    this.cartSubject.next({
      createdAt: new Date().toISOString(),
      items: [],
    });
  }

  private updateCart(cart: Cart) {
    this.cartSubject.next(cart);
    this.saveCart();
    this.calcCartTotal();
    this.cartHasSerializedItem = cart.items.some((item) => item.InventoryType === ItemInventoryType.SERIALIZED);
    this.cartHasAccessoryItem = cart.items.some((item) => item.InventoryType === ItemInventoryType.ACCESSORY);
  }

  public updateItemCartQuantity(item: ItemForDisplay, quantity = 1) {
    if (!this.cartSubject.value.createdAt) {
      this.createCart();
    }

    const cart = this.cartSubject.value;
    const itemInCart = cart.items.find(i => i.Pk === item.Pk);
    if (!itemInCart) {
      cart.items.push({
        ...item,
        cartQty: quantity,
      });
    } else {
      itemInCart.cartQty = quantity;
    }

    this.updateCart(cart);

    this.gaService.emitEvent(GAEventAction.ADD_TO_CART, {
      currency: 'USD',
      value: getItemPrice(item, this.appStateService.getUserPriceLevel())! * quantity,
      items: [{
        item_id: item.Model,
        item_name: item.SETitle || item.Model,
        item_brand: item.Manufacturer,
        price: getItemPrice(item, this.appStateService.getUserPriceLevel()),
        quantity: quantity,
      }]
    });
  }

  public removeItemFromCart(item: ItemForDisplay) {
    const cart = this.cartSubject.value;
    const items = cart.items?.filter(i => i.Pk !== item.Pk);
    cart.items = items;
    this.updateCart(cart);

    this.gaService.emitEvent(GAEventAction.REMOVE_FROM_CART, {
      currency: 'USD',
      value: getItemPrice(item, this.appStateService.getUserPriceLevel())! * (item.cartQty || 1),
      items: [{
        item_id: item.Model,
        item_name: item.SETitle || item.Model,
        item_brand: item.Manufacturer,
        price: getItemPrice(item, this.appStateService.getUserPriceLevel()),
        quantity: item.cartQty,
      }]
    });
  }

  private calcCartTotal() {
    const total = this.cartSubject.value.items?.reduce((acc, item) => {
      return acc + (getItemPrice(item, this.appStateService.getUserPriceLevel())! * item.cartQty!)
    }, 0) || 0;

    this.cartTotalSubject.next(total);
  }

  public reset() {
    localStorage.removeItem(CART_NAME);
    this.cartSubject.next({ items: [] });
    this.cartTotalSubject.next(0);
    this.cartHasAccessoryItem = false;
    this.cartHasSerializedItem = false;
  }

  public gaEmitViewCart() {
    const items: any[] = [];
    this.getCart().items.forEach((item) => {
      items.push({
        item_id: item.Model,
        item_name: item.SETitle || item.Model,
        item_brand: item.Manufacturer,
        price: getItemPrice(item, this.appStateService.getUserPriceLevel()),
        quantity: item.cartQty,
      });
    });

    this.gaService.emitEvent(GAEventAction.VIEW_CART, {
      currency: 'USD',
      value: this.getCartTotal(),
      items: items,
    });
  }
}
